import MEmptyDataPlaceholder from '../../../components/Monetize/MEmptyDataPlaceholder';
import { MSkeleton, MVStack } from '../../../components/Monetize';
import { getAccountQuotesRoute } from '../../../constants/routes';
import {
  ContractLineageWithBillGroups,
  useRenewalQuotesByLineage,
} from '../../../hooks/useContractLineages';
import { ContractLineageCard } from './ContractLineageCard';

interface ContractLineageCardsProps {
  accountId: string;
  lineages: ContractLineageWithBillGroups[];
  today: Date;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Omit when the user cannot open contracts; terms render disabled */
  onSelectContract?: (contractId: string) => void;
  canManageInvoices?: boolean;
}

export const ContractLineageCards = ({
  accountId,
  lineages,
  today,
  isLoading,
  isError,
  onRetry,
  onSelectContract,
  canManageInvoices,
}: ContractLineageCardsProps) => {
  const renewalQuotesByLineageId = useRenewalQuotesByLineage({
    accountId,
    lineages,
  });

  if (isLoading) {
    return (
      <MVStack gap={3} align="stretch">
        <MSkeleton h="120px" borderRadius="md" />
        <MSkeleton h="64px" borderRadius="md" />
        <MSkeleton h="64px" borderRadius="md" />
      </MVStack>
    );
  }

  if (isError) {
    return (
      <MEmptyDataPlaceholder
        mainMessage="We couldn't load contracts for this account."
        btnLabel="Try Again"
        onClick={onRetry}
      />
    );
  }

  if (lineages.length === 0) {
    return (
      <MEmptyDataPlaceholder
        mainMessage="Looks like there are no contracts here."
        btnLabel="View Quotes"
        to={getAccountQuotesRoute(accountId)}
      />
    );
  }

  return (
    <MVStack gap={3} align="stretch" data-testid="contract-lineage-cards">
      {lineages.map((lineage) => (
        <ContractLineageCard
          key={lineage.id}
          lineage={lineage}
          today={today}
          onSelectContract={onSelectContract}
          canManageInvoices={canManageInvoices}
          renewalQuotes={renewalQuotesByLineageId.get(lineage.id)}
        />
      ))}
    </MVStack>
  );
};
